import React, { memo, useCallback, useContext, useEffect, useState } from 'react'
import tw from 'twin.macro'
import { FetchMCPayments, ProcessMCPayments } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { showToast } from 'src/utils/toast'
import { Button } from 'src/components/shared/Button'
import { useDispatch, useSelector } from 'react-redux'
import { MarkPaidMinor } from '@shopify/polaris-icons'
import { SubmitConfirmModal } from 'src/components/shared/SubmitConfirmModal'
import { addS, promiseWithMinimalTime } from 'src/utils/baseUtils'
import { Typography } from '@mui/material'
import { colors } from 'src/theme'
import { handleApiError } from 'src/utils/errorHandlers'
import { ReloadWrapperContext } from '../wrapper/reload-wrapper/ReloadWrapper.context'
import { MCPayment } from 'src/interfaces/mcPayment'
import { PaymentStatus } from 'src/constants/payment/enums'
import { getSelectedMarket } from 'src/selectors/market'

const ProcessMCPaymentsButtonComponent = () => {
  const dispatch = useDispatch()

  const selectedMarket = useSelector(getSelectedMarket)

  const { reload } = useContext(ReloadWrapperContext)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [notPaidMCPaymentsAmount, setNotPaidMCPaymentsAmount] = useState<number>(0)

  useEffect(() => {
    const fetchMCPayments = async () => {
      setIsLoading(true)

      try {
        const payload: { data: Array<MCPayment> } = await createAsyncAction(
          dispatch,
          FetchMCPayments.request({}),
        )

        setNotPaidMCPaymentsAmount(
          payload.data.filter((item) => item.status === PaymentStatus.NotPaid).length,
        )
      } catch (err: unknown) {
        handleApiError(err)
      }

      setIsLoading(false)
    }

    fetchMCPayments()
  }, [selectedMarket?.id])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const handleSubmit = useCallback(async () => {
    handleCloseModal()

    setIsLoading(true)

    try {
      await promiseWithMinimalTime(createAsyncAction(dispatch, ProcessMCPayments.request()), 2000)

      reload()

      showToast('MCPayments processed successfully!')
    } catch (err: unknown) {
      handleApiError(err)
    }

    setIsLoading(false)
  }, [reload])

  return (
    <>
      <Button
        size="small"
        color="dark"
        loading={isLoading}
        rightIcon={<MarkPaidMinor width={20} height={20} fill={colors.BASICS_WHITE} />}
        disabled={!notPaidMCPaymentsAmount}
        className={tw`ml-2`}
        onClick={handleOpenModal}
      >
        Process MC Payments
      </Button>
      <SubmitConfirmModal
        isOpen={isModalOpen}
        title={`Process ${addS(notPaidMCPaymentsAmount, 'payment')}`}
        text={
          <Typography variant="h6" color={colors.SHADES_GREY_400}>
            {` Are you sure you want to process ${addS(notPaidMCPaymentsAmount, 'payment')}?`}
            <div css={tw`flex gap-1`}>
              <Typography variant="h6" color={colors.SHADES_GREY_400}>
                {`All ${notPaidMCPaymentsAmount} ${addS(notPaidMCPaymentsAmount, 'payment')} with`}
              </Typography>
              <Typography variant="h6" color={colors.COLORS_BLUE_500}>
                'Not paid'
              </Typography>
              <Typography variant="h6" color={colors.SHADES_GREY_400}>{`status will`}</Typography>
            </div>
            <Typography variant="h6" color={colors.SHADES_GREY_400}>{`process`}</Typography>
          </Typography>
        }
        submitLabel="Process"
        className={tw`w-[320px] min-w-[320px]`}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
      />
    </>
  )
}

export const ProcessMCPaymentsButton = memo(ProcessMCPaymentsButtonComponent)
